import React from 'react'
import { Link } from 'react-router-dom'
import CoFunderCard from './CoFunderCard'
import OrgIdentitySetup from './OrgIdentitySetup'

/**
 * "Foundations that fund alongside yours" — the surface for GET /api/co-funders
 * (#61, [ADR-0014](docs/adr/0014-cofunder-leads-are-graded-not-gated.md)).
 *
 * ## The one rule this component exists to enforce
 *
 * **There are FIVE outcomes here and only one of them is a list.** The other
 * four look identical from a distance — an empty rectangle — and are four
 * completely different sentences:
 *
 * | state | what actually happened | what the reader must be told |
 * |---|---|---|
 * | `known: false` | no usable EIN on file, so we never ran the query | we could not LOOK |
 * | `in_graph: false` | the EIN is fine; no filing we hold names it | the FILINGS are silent about them |
 * | `seed_count: 0` + `excluded_seeds` | filings DO name them; every funder in those filings is a pass-through or a scholarship fund | we FOUND them, and their funders cannot be applied to |
 * | `leads: []` | we found their funders; nothing cleared the filters | nothing qualified THIS MONTH |
 * | `leads: [...]` | — | the leads |
 *
 * The third row is #121. It was folded into the second until 2026-08-27, which
 * told 31.2% of Massachusetts organizations with an EIN-matched funder that no
 * filing named them, and then offered them the EIN-correction control — advice
 * that could only make correct data worse.
 *
 * **None of them may ever render as "no co-funders".** Only 13% of funders
 * itemize recipient EINs in their 990s at all (ADR-0012), so an empty answer is
 * a hole in the IRS data — not evidence about who funds this nonprofit. A
 * client who reads "no foundations fund alongside yours" and believes it has
 * been talked out of real prospects by a data gap, which is a worse failure
 * than showing nothing at all.
 *
 * This is the same discipline `peer_evidence` and `cofunder_leads` follow on
 * the server; it has to hold here too, because the server's careful
 * three-valued answer is worth nothing if the template collapses it.
 *
 * ## Where the EIN prompt goes
 *
 * In the `known: false` panel, which is exactly where the answer would have
 * been — ADR-0012's "ask at the point where it unlocks something visible, not
 * buried in preferences".
 *
 * That panel now leads with the #62 resolver (`OrgIdentitySetup`) rather than a
 * bare field: it looks the organization up by the name and state already on the
 * profile and asks "Is this you?", falling back to the typed field
 * (`EinPrompt`) whenever it cannot answer. This is the home #65 gave the
 * resolver after ADR-0018 gated the route and removed its only caller.
 */

/** Shared frame for the three no-list states, so they can't drift apart visually. */
function EmptyPanel({ icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <div className="text-sm text-gray-500 max-w-md mx-auto space-y-2">
        {children}
      </div>
    </div>
  )
}

export default function CoFunders({ data, onRefresh, orgName, state, orgEin }) {
  // The dashboard swallows a failed fetch so this section can never break the
  // rest of the page. Nothing rendered is the right answer for a network error
  // — an error banner here would be indistinguishable from a real "no".
  if (!data) return null

  const leads = data.leads || []
  // #121. Funders of record we dropped before they could seed anything. When
  // this is non-empty and seed_count is 0, the filings DO name this client:
  // state 2b below, NOT state 2.
  const excludedSeeds = data.excluded_seeds || []
  const daf = excludedSeeds.filter((e) => e.reason === 'donor_advised_intermediary')
  const scholarships = excludedSeeds.filter((e) => e.reason === 'scholarship_fund')

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900">
        Foundations That Fund Alongside Yours
        {leads.length > 0 && (
          <span className="ml-2 text-sm font-normal text-gray-500">{leads.length}</span>
        )}
      </h2>
      <p className="text-xs text-gray-500 mt-1 mb-3">
        Built from your own IRS 990 record: the foundations that already fund you,
        and who else gives to the organizations they give to. No other grant
        database has an opinion about your organization, so none of them can
        answer this.
      </p>

      {/* ── 1. We could not look ─────────────────────────────────────────── */}
      {!data.known && (
        <EmptyPanel icon="🔑" title="Add your EIN to see who funds alongside you">
          <p>
            We work this out from the foundations whose 990 filings name your
            organization, so we need your EIN to find you in them. It is public
            information, and it is not used for matching, so nothing else about
            your account changes.
          </p>
          <OrgIdentitySetup
            orgName={orgName}
            state={state}
            onSaved={onRefresh}
          />
        </EmptyPanel>
      )}

      {/* ── 2. The filings are silent about this organization ───────────── */}
      {data.known && !data.in_graph && (
        <EmptyPanel icon="🔍" title="No filing we hold names your organization yet">
          <p>
            We could not find a grant to your EIN in any 990 we have imported.
            That is usually a gap in the data rather than a fact about you:{' '}
            <span className="font-medium text-gray-600">
              only about 13% of foundations itemize recipient EINs at all
            </span>
            , and 990s lag 6–18 months behind the money.
          </p>
          <p>
            It is worth double-checking the EIN on your{' '}
            <Link to="/preferences" className="text-brand-500 hover:underline">
              preferences page
            </Link>{' '}
            because a single wrong digit looks exactly like this. Otherwise this
            fills in on its own as new filings land.
          </p>
          {/* "A single wrong digit looks exactly like this" is advice the reader
              cannot act on without going and checking nine digits. If our own
              data has an EIN filed under their name, we can do the checking FOR
              them — and here a suggestion is by construction a different EIN
              from the stored one, because a stored EIN that appeared in any
              filing would have put this client in `in_graph: true`. Renders
              nothing at all when the resolver has nothing to offer, so the
              paragraph above stays the whole answer in the common case: a
              CORRECT EIN lands here too, since only ~13% of funders itemize
              recipient EINs. */}
          <OrgIdentitySetup
            mode="correct"
            currentEin={orgEin}
            orgName={orgName}
            state={state}
            onSaved={onRefresh}
          />
        </EmptyPanel>
      )}

      {/* ── 2b. The filings DO name them; every funder in them is unactionable ─
          #121. This used to fall through to state 2 above and tell the client
          no filing named their organization, which is false, and then invite
          them to correct an EIN that had just demonstrably matched. Measured
          on prod 2026-08-27: 3,683 of 11,790 Massachusetts organizations with
          an EIN-matched funder (31.2%) have ALL of theirs excluded, so this is
          a common state and not a corner case.

          Deliberately NO OrgIdentitySetup here. The EIN is known to be right —
          it matched these filings — so asking them to fix it would be advice
          that can only make correct data worse. */}
      {data.known &&
        data.in_graph &&
        data.seed_count === 0 &&
        excludedSeeds.length > 0 && (
          <EmptyPanel
            icon="🏦"
            title="Your funders of record do not take applications"
          >
            <p>
              We did find you. Your EIN appears in{' '}
              <span className="font-medium text-gray-600">
                {excludedSeeds.length}{' '}
                {excludedSeeds.length === 1 ? 'filing' : 'filings'}
              </span>
              , from{' '}
              <span className="font-medium text-gray-600">
                {excludedSeeds.map((e) => e.name).join(', ')}
              </span>
              .
            </p>
            {daf.length > 0 && (
              <p>
                {daf.length === 1 ? 'That one is' : 'Those are'} donor-advised
                fund {daf.length === 1 ? 'sponsor' : 'sponsors'}. The grants in
                their filings were directed by individual donors who hold
                accounts there, not by a foundation program, so there is no
                application to make and no giving pattern that leads to other
                foundations.
              </p>
            )}
            {scholarships.length > 0 && (
              <p>
                {scholarships.length === 1
                  ? 'That one awards'
                  : 'Those award'}{' '}
                scholarships to individual students rather than grants to
                organizations, so there is no application your organization
                could make.
              </p>
            )}
            <p>
              This is a fact about those funders, not a gap in our data and not
              a problem with your EIN. This section fills in as soon as a
              foundation that runs its own grant program files a grant to you.
            </p>
          </EmptyPanel>
        )}

      {/* ── 3. Seeds found, nothing survived the filters ─────────────────── */}
      {data.known && data.in_graph && data.seed_count > 0 && leads.length === 0 && (
        <EmptyPanel icon="🗂️" title="Nothing qualified this time">
          <p>
            We found{' '}
            <span className="font-medium text-gray-600">
              {data.seed_count}{' '}
              {data.seed_count === 1 ? 'foundation' : 'foundations'} of record
            </span>{' '}
            for your organization and looked at who else gives to the same
            grantees. None of them cleared our filters, because we only surface
            foundations that give in your state, filed recently, and have not
            said they refuse unsolicited requests.
          </p>
          <p>
            This is a filter result, not a verdict on the landscape. It changes
            monthly as new 990s are imported.
          </p>
        </EmptyPanel>
      )}

      {/* ── 4. The leads ─────────────────────────────────────────────────── */}
      {leads.length > 0 && (
        <>
          <div className="space-y-2">
            {leads.map(lead => (
              <CoFunderCard key={lead.funder_id} lead={lead} />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Based on {data.seed_count}{' '}
            {data.seed_count === 1 ? 'foundation' : 'foundations'} whose filings
            name your organization.
            {data.seed_count === 1 && (
              <> A single funder of record is thin evidence, so treat these as a
                starting point rather than a shortlist.</>
            )}{' '}
            These are leads from 990 filings, not open calls: check each
            foundation's own guidelines before approaching them.
          </p>
          {/* ADR-0014: the upgrade prompt has to be honest. `more_available`
              counts leads that PASSED every filter and were cut by the tier
              cap — never padded with ones that failed. */}
          {data.more_available && (
            <p className="text-xs text-gray-500 mt-2">
              Your plan shows {data.tier_limit}. More qualified:{' '}
              <Link to="/account" className="text-brand-500 hover:underline">
                see plans
              </Link>
              .
            </p>
          )}
        </>
      )}
    </div>
  )
}
